import { Box2 } from '@/libs/box2'
import { Pallet } from '@/generated/model/pallet'
import { ProgramSettings } from '@/generated/model/programSettings'
import * as THREE from 'three'
import { ViewPort } from '@/libs/pallet3D'
import { Box } from '@/generated/model/box'
import { basePath } from '@/main'
import { BoxPosition } from '@/generated/model/boxPosition'
import { Sequence } from '@/generated/model/sequence'
import { calculateCustomLabelPosition, findSequenceByBoxId, getCustomInfeedRotation, getSequenceBoxNumbers } from '@/libs/pattern/sequenceUtils'

const defWidth = 1600
// const defHeight = 1500
const LONG_SIDE_COLOR = 0xee99a4 // 0xf14e62 // 0xee99a4 // 0xf14e62 // 0xBA0C2F // 0xffaa99
const SHOT_SIDE_COLOR = 0x399eee // 0x005eb8 // 0x88aaff

export class Overview3D {
  viewPort: ViewPort = { h: 1100, w: 920 }
  private background: number = 0xfafafa
  private box: Box2
  private pallet: Pallet
  private programSettings: ProgramSettings
  private labelPos: ProgramSettings.LabelPositionEnum

  private container: HTMLElement
  private scene: THREE.Scene
  private camera: THREE.OrthographicCamera
  private renderer: THREE.WebGLRenderer
  private boxMaterials: THREE.Material[]
  private group: THREE.Group
  private font?: THREE.Font
  private layers: THREE.Group[] = []
  private numbersGroup: THREE.Group
  private boxesGroup: THREE.Group
  private shiftY = 1600
  private leftShiftX = -350

  constructor (elementID: string, box: Box, pallet: Pallet, programSettings: ProgramSettings,
               background?: number, viewPort?: ViewPort, layerSpace?: number) {
    this.background = background ? background : this.background
    this.pallet = pallet
    this.viewPort = viewPort ? viewPort : this.viewPort
    this.programSettings = programSettings
    this.labelPos = programSettings.labelPosition!
    this.box = new Box2(box)
    if (programSettings.boxOrientationOnConveyor === ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING) {
      this.box.setShortSideLeading()
    } else {
      this.box.setLongSideLeading()
    }

    let element = document.getElementById(elementID)
    if (!element) {
      console.log(`[ERROR] html element '${elementID}' not found`)
      element = document.createElement(elementID)
    } else {
      // clean element content
      while (element.firstChild) {
        element.firstChild.remove()
      }
    }
    const child = document.createElement(elementID + '_container')
    element.appendChild(child)

    this.container = child
    this.scene = this.sceneSetup()
    this.camera = this.cameraSetup()
    this.lightSetup()

    this.group = new THREE.Group()
    this.group.position.set(0, 0, 840)
    this.scene.add(this.group)

    this.boxesGroup = new THREE.Group()
    this.boxesGroup.position.set(0, 0, 0)
    this.group.add(this.boxesGroup)

    this.numbersGroup = new THREE.Group()
    this.numbersGroup.position.set(0, 0, 0)
    this.group.add(this.numbersGroup)

    this.boxMaterials = this.boxSetup()
    this.renderer = this.renderSetup()
    if (this.isCustomSolution()) {
      this.addReferenceFrame()
    }
    this.renderer.render(this.scene, this.camera)
  }

  setNumbers (positionsLeft: BoxPosition[], positionsRight: BoxPosition[], h: number,
              sequencesLeft?: Sequence[], sequencesRight?: Sequence[], debugBoxIds: boolean = false) {
    this.numbersGroup.children.length = 0
    const loader = new THREE.FontLoader()
    loader.load('DejaVuSans.json', (font) => {
      this.font = font
      const leftBoxNumbers = getSequenceBoxNumbers(sequencesLeft)
      const rightBoxNumbers = getSequenceBoxNumbers(sequencesRight)
      const hh = h + this.box.height
      positionsLeft.forEach((p, i) => {
        const num = leftBoxNumbers.get(Number(p.id))
        const text = this.addNumber({ ...p, x: this.getBoxX(p.x!) + this.leftShiftX }, debugBoxIds && p.id != null ? p.id : (num != null ? num : i + 1), hh)
        this.numbersGroup.add(text)
      })
      positionsRight.forEach((p, i) => {
        const num = rightBoxNumbers.get(Number(p.id))
        const text = this.addNumber({ ...p, y: p.y! + this.shiftY }, debugBoxIds && p.id != null ? p.id : (num != null ? num : i + 1), hh)
        this.numbersGroup.add(text)
      })
      this.renderer.render(this.scene, this.camera)
    })
  }

  sceneSetup (): THREE.Scene {
    const scene = new THREE.Scene()

    // invert coordinate system !!!
    const m = new THREE.Matrix4().fromArray([1, 0, 0, 0, 0, -1, 0, 0, 0, 0, 0.7, 0, 0, 0, 0, 1])
    scene.applyMatrix(m)
    scene.updateMatrix()
    scene.rotation.z = Math.PI + (Math.PI / 180 * 65)

    scene.background = new THREE.Color(this.background)
    scene.add(new THREE.AmbientLight(0xffffff))
    return scene
  }

  cameraSetup (): THREE.OrthographicCamera {
    const aspect = this.viewPort.w / this.viewPort.h

    const palletHypo = Math.floor((this.pallet.length! ** 2 + this.pallet.width! ** 2) ** (1 / 2))
    const scale = defWidth / this.viewPort.w
    // const scale = 1
    const viewSize = 2000 * (1 + 0.3 * (1280 / palletHypo - 1)) * scale * 1.2

    const camera = new THREE.OrthographicCamera(-aspect * viewSize / 2, aspect * viewSize / 2,
      viewSize / 2, -viewSize / 2, 1, 10000)

    camera.up = new THREE.Vector3(0, 0, 1)
    const k = 1.3
    camera.position.set(900, -2500, k * 2500)

    camera.lookAt(new THREE.Vector3(1300 * k, 800 * k, -300 * k))
    this.camera = camera // extra init for method
    return camera
  }

  lightSetup () {
    const light = new THREE.AmbientLight(0xffffff)
    this.scene.add(light)
  }

  renderSetup (): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setPixelRatio(window.devicePixelRatio)
    renderer.setSize(this.viewPort.w, this.viewPort.h)
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFShadowMap
    this.container.appendChild(renderer.domElement)
    renderer.render(this.scene, this.camera)
    return renderer
  }

  addHelpers () {
    // base surface
    const base = new THREE.PlaneGeometry(this.pallet.length, this.pallet.width)
    const obj = new THREE.Mesh(base, new THREE.MeshLambertMaterial({ color: 0x888888 }))
    obj.position.x = this.pallet.length! / 2 - 100
    obj.position.y = this.pallet.width! / 2 + 100
    this.scene.add(obj)

    // axes
    const axesHelper = new THREE.AxesHelper(2000)
    this.scene.add(axesHelper)

    // background
    this.scene.background = new THREE.Color(0xccfafa)
  }

  // TODO: refactor it, copy from pallet3d
  addBox (x: number, y: number, z: number, rot: number, failedCheck: boolean,
          labelPosition?: ProgramSettings.LabelPositionEnum): THREE.Group {
    const boxGroup = new THREE.Group()
    const geometry = new THREE.BoxBufferGeometry(this.box.sideLength, this.box.frontLength, this.box.height)
    const materials = (failedCheck) ? this.errorBoxMaterials() : this.boxMaterials
    const boxObject = new THREE.Mesh(geometry, materials)
    boxGroup.add(boxObject)
    this.setLabel(labelPosition || this.programSettings.labelPosition!, boxGroup)
    boxGroup.position.x = x
    boxGroup.position.y = y
    boxGroup.position.z = z
    boxGroup.rotation.x = 0
    boxGroup.rotation.y = 0
    // convert from pallet coordinate system with anti-clockwise rotation to three.js rotation (clockwise radians)
    boxGroup.rotation.z = -rot / 180 * Math.PI
    boxGroup.scale.x = 1
    boxGroup.scale.y = 1
    boxGroup.scale.z = 1
    boxGroup.castShadow = true
    boxGroup.receiveShadow = true
    return boxGroup
  }

  addLayer (patternLeft: BoxPosition[], patternRight: BoxPosition[], h: number, idErrorBox?: number,
            sequencesLeft?: Sequence[]) {
    this.boxesGroup.children.length = 0
    h = h + this.box.height / 2

    patternLeft.forEach(point => {
      const box = this.addBox(this.getBoxX(point.x!) + this.leftShiftX, point.y!, 0, point.rotation!,
        (idErrorBox != null && idErrorBox === point.lpbuId), this.getLabelPosition(point, sequencesLeft))
      this.boxesGroup.add(box)
    })
    patternRight.forEach(point => {
      const box = this.addBox(point.x!, point.y! + this.shiftY, 0, point.rotation!, (idErrorBox != null && idErrorBox === point.lpbuId))
      this.boxesGroup.add(box)
    })

    this.boxesGroup.position.z = h
    this.group.add(this.boxesGroup)
    if (this.renderer) {
      this.renderer.render(this.scene, this.camera)
    }
  }

  boxSetup (): THREE.Material[] {
    const boxTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box.jpg`, () => this.renderer.render(this.scene, this.camera))

    const boxTopTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box-top.jpg`, () => this.renderer.render(this.scene, this.camera))

    // noinspection JSUnusedLocalSymbols
    const boxLabeledTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box-labeled.jpg`, () => this.renderer.render(this.scene, this.camera))

    const emptySide = new THREE.MeshStandardMaterial({ map: boxTexture, roughness: 1 })
    const longSide = new THREE.MeshStandardMaterial({ color: LONG_SIDE_COLOR, map: boxTexture, roughness: 0 })
    const shortSide = new THREE.MeshStandardMaterial({ color: SHOT_SIDE_COLOR, map: boxTexture, roughness: 0 })
    const topSide = new THREE.MeshStandardMaterial({ map: boxTopTexture, roughness: 0.9 })

    return [shortSide, shortSide, longSide, longSide, topSide, emptySide]
  }

  errorBoxMaterials (): THREE.Material[] {
    const boxTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box.jpg`, () => this.renderer.render(this.scene, this.camera))

    const boxTopTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box-top.jpg`, () => this.renderer.render(this.scene, this.camera))

    const emptySide = new THREE.MeshStandardMaterial({ color: 0xff0000, map: boxTexture, roughness: 1 })
    const longSide = new THREE.MeshStandardMaterial({ color: 0xff0000, map: boxTexture, roughness: 0 })
    const shortSide = new THREE.MeshStandardMaterial({ color: 0xff0000, map: boxTexture, roughness: 0 })
    const topSide = new THREE.MeshStandardMaterial({ color: 0xff0000, map: boxTopTexture, roughness: 0.9 })

    return [shortSide, shortSide, longSide, longSide, topSide, emptySide]
  }

  private addReferenceFrame () {
    const origin = new THREE.Vector3(this.leftShiftX + this.pallet.length!, 0, 8)
    const axisLength = Math.min(this.pallet.length!, this.pallet.width!) * 0.35
    const color = 0xff0000
    const material = new THREE.LineBasicMaterial({ color })
    const xAxis = new THREE.Line(new THREE.BufferGeometry().setFromPoints([
      origin,
      new THREE.Vector3(origin.x - axisLength, origin.y, origin.z)
    ]), material)
    const yAxis = new THREE.Line(new THREE.BufferGeometry().setFromPoints([
      origin,
      new THREE.Vector3(origin.x, origin.y + axisLength, origin.z)
    ]), material)
    const xArrow = new THREE.ArrowHelper(new THREE.Vector3(-1, 0, 0), origin, axisLength, color, axisLength * 0.18, axisLength * 0.08)
    const yArrow = new THREE.ArrowHelper(new THREE.Vector3(0, 1, 0), origin, axisLength, color, axisLength * 0.18, axisLength * 0.08)
    const originMarker = new THREE.Mesh(
      new THREE.SphereGeometry(axisLength * 0.035, 16, 16),
      new THREE.MeshBasicMaterial({ color })
    )
    originMarker.position.copy(origin)
    this.group.add(xAxis, yAxis, xArrow, yArrow, originMarker)
  }

  private getLabelPosition (point: BoxPosition, sequences?: Sequence[]): ProgramSettings.LabelPositionEnum {
    const labelPosition = this.programSettings.labelPosition!
    if (this.programSettings.robotType !== 'CUSTOM_SOLUTION') {
      return labelPosition
    }
    if (!sequences || sequences.length === 0 || point.id == null) {
      return ProgramSettings.LabelPositionEnum.IGNORE
    }
    const sequence = findSequenceByBoxId(point.id, sequences)
    if (!sequence) {
      return ProgramSettings.LabelPositionEnum.IGNORE
    }
    const pick = sequence.pick!.find(p => (p.boxesGroup || []).includes(point.id!))
    const pickRotation = pick && pick.zRot != null ? pick.zRot : 0
    const placeRotation = sequence.place && sequence.place[0] && sequence.place[0].zRot != null ? sequence.place[0].zRot : 0
    const boxRotation = point.rotation || 0
    return calculateCustomLabelPosition(
      labelPosition,
      this.programSettings.boxOrientationOnConveyor,
      this.programSettings.conveyorConfiguration,
      boxRotation,
      pickRotation,
      placeRotation,
      getCustomInfeedRotation(sequence)
    )
  }

  private getBoxX (x: number): number {
    return this.isCustomSolution() ? this.pallet.length! - x : x
  }

  private isCustomSolution (): boolean {
    return this.programSettings.robotType === 'CUSTOM_SOLUTION'
  }

  // TODO: refactor it, copy from pallet3D
  private makeLabel (): THREE.Group {
    const L = 100
    const W = 45
    const geometry = new THREE.PlaneGeometry(L, W)
    const paper = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: 0xeeeeee }))
    const group = new THREE.Group()
    group.add(paper)
    group.position.x = L / 2
    group.position.y = W / 2
    group.position.z = 200
    group.name = 'label'
    return group
  }

  private addNumber (position: BoxPosition, num: number, height: number): THREE.Object3D {
    const textValue = num.toString()
    const size = this.getNumberFontSize(textValue)
    const material = new THREE.LineBasicMaterial({ color: 0x000000, side: THREE.DoubleSide })
    const shapes = this.font!.generateShapes(textValue, size, 5)
    const geometry = new THREE.ShapeBufferGeometry(shapes)
    geometry.computeBoundingBox()
    const bounds = geometry.boundingBox
    const text = new THREE.Mesh(geometry, material)
    // text.rotateX(Math.PI)
    text.rotation.x = Math.PI
    text.position.x = position.x! - (bounds ? (bounds.min.x + bounds.max.x) / 2 : size / 2)
    text.position.y = position.y! + (bounds ? (bounds.min.y + bounds.max.y) / 2 : size / 2)
    text.position.z = height + 3
    // text.rotateZ(-position!.rotation!)
    // boxGroup.add(text)
    return text
  }

  private getNumberFontSize (text: string): number {
    const shortestSide = Math.min(this.box.sideLength, this.box.frontLength)
    const baseSize = Math.min(58, Math.floor(shortestSide * 0.28))
    const sizeByLength = text ? Math.floor(shortestSide * 0.7 / text.length * 1.3) : baseSize
    return Math.max(22, Math.min(baseSize, sizeByLength))
  }

  // TODO: refactor it, copy from pallet3D
  private setLabel (pos: ProgramSettings.LabelPositionEnum, boxGroup: THREE.Group) {
    const labelA = this.makeLabel()
    const labelB = this.makeLabel()
    this.labelPos = pos
    const length = 100
    const frontLength = this.box.frontLength
    const sideLength = this.box.sideLength

    const front = (label: THREE.Object3D) => {
      label.rotation.x = 0
      label.rotation.y = -Math.PI / 2
      label.rotation.z = Math.PI / 2
      label.position.x = -(sideLength / 2 + 1)
      label.position.y = 0
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    const back = (label: THREE.Object3D) => {
      label.rotation.x = 0
      label.rotation.y = Math.PI / 2
      label.rotation.z = Math.PI / 2
      label.position.x = +(sideLength / 2 + 1)
      label.position.y = 0
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    const right = (label: THREE.Object3D) => {
      label.rotation.x = Math.PI / 2
      label.rotation.y = 0
      label.rotation.z = 0
      label.position.x = 0
      label.position.y = -(frontLength / 2 + 1)
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    const left = (label: THREE.Object3D) => {
      label.rotation.x = -Math.PI / 2
      label.rotation.y = 0
      label.rotation.z = 0
      label.position.x = 0
      label.position.y = +(frontLength / 2 + 1)
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    switch (pos) {
      case ProgramSettings.LabelPositionEnum.FRONT:
        // boxGroup.remove(labelB)
        boxGroup.add(labelA)
        front(labelA)
        break

      case ProgramSettings.LabelPositionEnum.RIGHT:
        // boxGroup.remove(labelB)
        boxGroup.add(labelA)
        right(labelA)
        break

      case ProgramSettings.LabelPositionEnum.FRONT_RIGHT_CORNER:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        boxGroup.add(labelB)
        right(labelA)
        front(labelB)
        labelA.position.x = -(sideLength / 2 - length / 2)
        labelB.position.y = -(frontLength / 2 - length / 2)
        break

      case ProgramSettings.LabelPositionEnum.LEFT:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        left(labelA)
        break

      case ProgramSettings.LabelPositionEnum.BACK:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        back(labelA)
        break

      case ProgramSettings.LabelPositionEnum.RIGHT_BACK_CORNER:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        boxGroup.add(labelB)
        right(labelA)
        back(labelB)
        labelA.position.x = +(sideLength / 2 - length / 2)
        labelB.position.y = -(frontLength / 2 - length / 2)
        break

      case ProgramSettings.LabelPositionEnum.LEFT_FRONT_CORNER:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        boxGroup.add(labelB)
        left(labelA)
        front(labelB)
        labelA.position.x = -(sideLength / 2 - length / 2)
        labelB.position.y = +(frontLength / 2 - length / 2)
        break

      case ProgramSettings.LabelPositionEnum.BACK_LEFT_CORNER:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        boxGroup.add(labelB)
        left(labelA)
        back(labelB)
        labelA.position.x = +(sideLength / 2 - length / 2)
        labelB.position.y = +(frontLength / 2 - length / 2)
        break
    }
    // this.render()
  }
}
